"use client";

import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  Globe,
  Heart,
  Instagram,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Share2,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import { postSourceLabel } from '@/lib/social-media/post-source';
import { Fragment } from 'react';

export type SocialPostRow = {
  _id: string;
  postId: string;
  platform?: 'facebook' | 'instagram';
  instagram_media_id?: string;
  instagramUsername?: string;
  message: string;
  links: string[];
  tags: string[];
  mediaUrl: string | null;
  postType: string;
  status: string;
  postDate: string | null;
  likesCount: number;
  commentsCount?: number;
  viewsCount: number;
  permalink: string;
  postSource?: 'admin' | 'blog' | 'thought_leadership' | 'news' | 'press_release' | 'facebook' | 'instagram';
  failureError?: string | null;
  linkedBlog?: {
    _id: string;
    title: string;
    slug: string;
    status: string;
    isDeleted: boolean;
    contentKind?: 'blog' | 'thought_leadership' | 'news' | 'press_release';
  } | null;
  blogDeleted?: boolean;
};

const PAGE_NAME = process.env.NEXT_PUBLIC_FACEBOOK_PAGE_NAME || 'Facebook Page';
const PAGE_ID = process.env.NEXT_PUBLIC_FACEBOOK_PAGE_ID?.trim() || '';
const INSTAGRAM_USERNAME =
  process.env.NEXT_PUBLIC_INSTAGRAM_USERNAME ||
  process.env.NEXT_PUBLIC_INSTAGRAM_PAGE_NAME ||
  'Instagram';

function pageAvatarUrl(): string | null {
  if (!PAGE_ID) return null;
  return `https://graph.facebook.com/${encodeURIComponent(PAGE_ID)}/picture?type=small`;
}

function shortRelative(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w`;
  return new Date(iso).toLocaleDateString();
}

/** Split caption into plain segments and hashtags (Facebook-style blue). */
function renderCaptionSegments(text: string) {
  const parts = text.split(/(\s#[^\s#]+|#[^\s#]+)/g);
  return parts.map((chunk, i) => {
    if (/^#/.test(chunk.trim()) || /^\s#/.test(chunk)) {
      const t = chunk.trim();
      return (
        <span key={i} className="text-[#0866FF] font-medium hover:underline cursor-default">
          {chunk.startsWith(' ') ? ' ' : ''}
          {t}
        </span>
      );
    }
    return <Fragment key={i}>{chunk}</Fragment>;
  });
}

function looksLikeDirectImageOrVideoStill(u: string): boolean {
  if (!/^https?:\/\//i.test(u)) return false;
  if (/fbcdn\.net|scontent|graph\.facebook\.com|\.jpg|\.jpeg|\.png|\.gif|\.webp/i.test(u)) return true;
  try {
    const host = new URL(u).hostname;
    if (!/facebook\.com$/i.test(host)) return true;
    return /picture|safe_image|photos/i.test(u);
  } catch {
    return false;
  }
}

function renderInstagramCaptionSegments(text: string) {
  const parts = text.split(/(\s#[^\s#]+|#[^\s#]+)/g);
  return parts.map((chunk, i) => {
    if (/^#/.test(chunk.trim()) || /^\s#/.test(chunk)) {
      const t = chunk.trim();
      return (
        <span key={i} className="text-[#385185] font-medium hover:underline cursor-default">
          {chunk.startsWith(' ') ? ' ' : ''}
          {t}
        </span>
      );
    }
    return <Fragment key={i}>{chunk}</Fragment>;
  });
}

function linkedContentLabel(
  kind: 'blog' | 'thought_leadership' | 'news' | 'press_release' | undefined
): string {
  if (kind === 'thought_leadership') return 'Thought & Leadership';
  if (kind === 'news') return 'News';
  if (kind === 'press_release') return 'Press Release';
  return 'Blog';
}

export default function SocialMediaViewModal({
  post,
  open,
  onOpenChange,
  showAdminToolbar,
  onAdminEdit,
  onAdminDelete,
  adminDeleteLabel,
  adminDeleteTitle,
  failureActionLabel,
  onFailureAction,
}: {
  post: SocialPostRow | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** When set, show Edit / Delete controls for admin (e.g. blog management). */
  showAdminToolbar?: boolean;
  onAdminEdit?: () => void;
  onAdminDelete?: () => void;
  adminDeleteLabel?: string;
  adminDeleteTitle?: string;
  failureActionLabel?: string;
  onFailureAction?: () => void;
}) {
  const avatar = pageAvatarUrl();

  if (!post) return null;

  const showMedia = post.mediaUrl && looksLikeDirectImageOrVideoStill(post.mediaUrl);
  const canEditPost = post.platform !== 'instagram';
  const isInstagramPost = post.platform === 'instagram';

  const relative = shortRelative(post.postDate);

  if (isInstagramPost) {
    const instagramName = post.instagramUsername?.trim() || INSTAGRAM_USERNAME;
    const media = post.mediaUrl;
    const isVideo = post.postType === 'video';

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/55 z-50 data-[state=open]:animate-in fade-in" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(96vw,470px)] max-h-[92vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-2xl border border-gray-200 focus:outline-none">
            <div className="sr-only">
              <Dialog.Title>Instagram post preview</Dialog.Title>
              <Dialog.Description>Post as shown on Instagram</Dialog.Description>
            </div>

            <div className="bg-white text-[14px] leading-snug text-[#262626]">
              <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
                <div className="rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 p-[2px]">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                    <Instagram className="h-5 w-5 text-pink-600" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-[#262626]">{instagramName}</div>
                  <div className="text-xs text-[#8e8e8e]">{relative || 'Instagram'}</div>
                </div>
                <button type="button" className="rounded-full p-1.5 text-[#262626] hover:bg-gray-100" aria-label="More">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
                <Dialog.Close
                  className="rounded-full p-1.5 text-[#262626] hover:bg-gray-100"
                  aria-label="Close Instagram preview"
                >
                  <X className="h-5 w-5" />
                </Dialog.Close>
              </div>

              <div className="aspect-square w-full bg-[#fafafa] flex items-center justify-center overflow-hidden">
                {media ? (
                  isVideo ? (
                    <video
                      src={media}
                      controls
                      className="h-full w-full object-contain bg-black"
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={media}
                      alt=""
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  )
                ) : (
                  <div className="flex flex-col items-center gap-2 text-[#8e8e8e]">
                    <Instagram className="h-12 w-12" />
                    <span className="text-sm">Media unavailable</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 px-4 py-3">
                <div className="flex items-center gap-4">
                  <Heart className="h-6 w-6 text-[#262626]" />
                  <MessageCircle className="h-6 w-6 text-[#262626]" />
                  <Share2 className="h-6 w-6 text-[#262626]" />
                </div>
                <div className="font-semibold text-[#262626]">
                  {(post.likesCount || 0).toLocaleString()} likes
                </div>
                {(post.message || post.tags.length > 0) && (
                  <div className="whitespace-pre-wrap break-words">
                    <span className="font-semibold">{instagramName}</span>
                    {post.message ? <span> {renderInstagramCaptionSegments(post.message)}</span> : null}
                    {post.tags.length > 0 && (
                      <span className="text-[#385185] font-medium">
                        {post.message ? '\n' : ' '}
                        {post.tags.map((t, idx) => (
                          <span key={`${t}-${idx}`}>
                            {idx > 0 ? ' ' : ''}
                            {t.startsWith('#') ? t : `#${t}`}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                )}
                {(post.commentsCount ?? 0) > 0 ? (
                  <div className="text-[#8e8e8e]">
                    View all {(post.commentsCount ?? 0).toLocaleString()} comments
                  </div>
                ) : null}
                <div className="text-[11px] uppercase tracking-wide text-[#8e8e8e]">
                  {post.postDate ? new Date(post.postDate).toLocaleDateString() : 'Date unavailable'}
                </div>
              </div>

              {(post.postSource || post.linkedBlog || post.permalink || post.postId) && (
                <div className="border-t border-gray-100 bg-[#fafafa] px-4 py-3 text-[13px]">
                  <div className="flex flex-wrap gap-2">
                    {post.postSource ? (
                      <span className="rounded-full bg-pink-50 text-pink-700 px-2 py-0.5 font-semibold">
                        {postSourceLabel(post.postSource)}
                      </span>
                    ) : null}
                    {post.linkedBlog && !post.blogDeleted ? (
                      <span className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 font-medium">
                        {linkedContentLabel(post.linkedBlog.contentKind)}: {post.linkedBlog.title}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {post.permalink ? (
                      <a
                        href={post.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-pink-700 hover:underline"
                      >
                        Open on Instagram
                      </a>
                    ) : null}
                    {post.postId ? (
                      <span className="font-mono text-[11px] text-[#8e8e8e] break-all">
                        Media id: {post.instagram_media_id || post.postId}
                      </span>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(96vw,500px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-lg bg-[#f0f2f5] p-3 shadow-xl focus:outline-none">
          <div className="sr-only">
            <Dialog.Title>Facebook post preview</Dialog.Title>
            <Dialog.Description>Post as shown on Facebook</Dialog.Description>
          </div>

          <div className="bg-white rounded-lg border border-[#dddfe2] shadow-sm overflow-hidden text-[15px] leading-snug text-[#050505]">
            {/* Header */}
            <div className="flex items-start gap-2 px-3 pt-3 pb-2">
              <div className="h-10 w-10 shrink-0 rounded-full overflow-hidden bg-[#e4e6eb] border border-[#dddfe2]">
                {avatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-500">
                    {PAGE_NAME.slice(0, 1)}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[15px] leading-5">{PAGE_NAME}</div>
                <div className="flex items-center gap-1 text-[13px] text-[#65676b] mt-0.5">
                  <span>{relative || '—'}</span>
                  <span aria-hidden>·</span>
                  <Globe className="h-3.5 w-3.5" aria-label="Public" />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Dialog.Close className="rounded-full p-1.5 text-[#65676b] hover:bg-[#f0f2f5]">
                  <X className="h-5 w-5" />
                </Dialog.Close>
                <button type="button" className="rounded-full p-1.5 text-[#65676b] hover:bg-[#f0f2f5]" aria-label="More">
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>

            {post.failureError ? (
              <div
                className="mx-3 mb-2 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-[13px] text-red-900"
                role="alert"
              >
                <div className="flex gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">Facebook publish failed</p>
                    <p className="mt-1 text-red-800/90 break-words">{post.failureError}</p>
                    {onFailureAction ? (
                      <button
                        type="button"
                        onClick={onFailureAction}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        {failureActionLabel || 'Discard failed post'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {post.blogDeleted && post.linkedBlog ? (
              <div
                className="mx-3 mb-2 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-[13px] text-red-900"
                role="alert"
              >
                <div className="flex gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold">Linked content was deleted</p>
                    <p className="mt-1 text-red-800/90">
                      &quot;{post.linkedBlog.title}&quot; is no longer on the site. Update this Facebook
                      post&apos;s caption and links, or delete the post from Social Media Management.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {post.postSource || post.linkedBlog ? (
              <div className="px-3 pb-2 flex flex-wrap gap-2 text-[13px]">
                {post.postSource ? (
                  <span className="rounded-full bg-[#e7f3ff] text-[#0866FF] px-2 py-0.5 font-semibold">
                    {postSourceLabel(post.postSource)}
                  </span>
                ) : null}
                {post.linkedBlog && !post.blogDeleted ? (
                  <span className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 font-medium">
                    {linkedContentLabel(post.linkedBlog.contentKind)}: {post.linkedBlog.title}
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* Caption */}
            {(post.message || post.tags.length > 0) && (
              <div className="px-3 pb-2 text-[15px] whitespace-pre-wrap break-words">
                {post.message ? renderCaptionSegments(post.message) : null}
                {post.tags.length > 0 && (
                  <span className="text-[#0866FF] font-medium">
                    {post.message ? '\n\n' : ''}
                    {post.tags.map((t, idx) => (
                      <span key={t}>
                        {idx > 0 ? ' ' : ''}
                        {t.startsWith('#') ? t : `#${t}`}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            )}

            {/* Media */}
            {(showMedia || post.postType === 'photo' || post.postType === 'video') && post.mediaUrl && (
              <div className="border-y border-[#dddfe2] bg-black/[0.03]">
                {showMedia ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={post.mediaUrl}
                    alt=""
                    className="w-full max-h-[min(70vh,520px)] object-contain bg-[#f0f2f5]"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="px-3 py-8 text-center text-[#65676b] text-sm">
                    Preview URL is not a direct image link.{' '}
                    <a href={post.permalink} target="_blank" rel="noreferrer" className="text-[#0866FF] font-semibold">
                      View on Facebook
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Engagement bar */}
            <div className="flex items-center justify-between px-3 py-2 text-[15px] text-[#65676b]">
              <div className="flex items-center gap-1.5 min-h-[22px]">
                {post.likesCount > 0 ? (
                  <>
                    <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#0866FF] text-white">
                      <ThumbsUp className="h-2.5 w-2.5 fill-current" strokeWidth={0} />
                    </span>
                    <span className="text-[#65676b]">{post.likesCount.toLocaleString()}</span>
                  </>
                ) : (
                  <span className="text-[13px]">Be the first to like this</span>
                )}
              </div>
              {post.viewsCount > 0 && (
                <span className="text-[13px]">{post.viewsCount.toLocaleString()} views</span>
              )}
            </div>

            <div className="h-px bg-[#ced0d4] mx-3" />

            {showAdminToolbar && (onAdminEdit || onAdminDelete) && (
              <div className="px-3 py-2 flex flex-wrap gap-2 bg-white border-t border-[#e7e8eb]">
                {onAdminEdit && canEditPost && (
                  <button
                    type="button"
                    onClick={onAdminEdit}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#0866FF] px-3 py-1.5 text-sm font-semibold text-[#0866FF] hover:bg-[#e7f3ff]"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </button>
                )}
                {onAdminDelete && (
                  <button
                    type="button"
                    title={adminDeleteTitle || adminDeleteLabel || 'Delete'}
                    onClick={onAdminDelete}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    {adminDeleteLabel || 'Delete'}
                  </button>
                )}
              </div>
            )}

            {/* Actions row */}
            <div className="grid grid-cols-3 px-2 py-1">
              {[
                { Icon: ThumbsUp, label: 'Like' },
                { Icon: MessageCircle, label: 'Comment' },
                { Icon: Share2, label: 'Share' },
              ].map(({ Icon, label }) => (
                <button
                  key={label}
                  type="button"
                  className="flex items-center justify-center gap-2 py-2 rounded-md text-[#65676b] hover:bg-[#f0f2f5] text-[15px] font-semibold"
                >
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            <div className="h-px bg-[#ced0d4]" />

            {/* Links (admin detail below FB chrome) */}
            {(post.links.length > 0 || post.permalink) && (
              <div className="px-3 py-2.5 space-y-2 bg-[#fafbfc] text-[13px] border-t border-[#e7e8eb]">
                {post.links.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#65676b] mb-1">Links</div>
                    <ul className="space-y-1">
                      {post.links.map((l) => (
                        <li key={l}>
                          <a href={l} target="_blank" rel="noreferrer" className="text-[#0866FF] hover:underline break-all">
                            {l}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {post.permalink && (
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block text-[#0866FF] font-semibold hover:underline"
                  >
                    Open on Facebook
                  </a>
                )}
                {post.postId && (
                  <p className="text-[11px] font-mono text-[#65676b] break-all">Post id: {post.postId}</p>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
